/**
 * Réseau routier automatique (§7) — **logique pure**.
 *
 * Le joueur ne trace pas de routes : il pose un bâtiment, et le jeu raccorde la case au
 * réseau existant. Zéro friction, et la ville se dessine d'elle-même autour du bâti.
 *
 * Trois décisions structurantes.
 *
 * **Les routes sont un ÉTAT, pas une dérivée.** On pourrait recalculer le réseau à
 * chaque affichage à partir des bâtiments — mais le tracé dépend de l'ORDRE des poses,
 * puisque chaque nouveau bâtiment se raccorde à ce qui existait avant lui. Une dérivée
 * réordonnerait tout le réseau au moindre changement, et la ville que le joueur a
 * composée se réorganiserait sous ses yeux. Le réseau est donc sauvegardé.
 *
 * **Une rue doit toujours pouvoir GRANDIR — c'est une règle, pas une préférence.**
 *
 * Cette ligne disait autrefois que « les couloirs sont réservés avant de proposer un
 * emplacement ». C'était faux : rien ne réservait quoi que ce soit, et le placement
 * automatique faisait exactement l'inverse de ce qui était écrit — il récompensait
 * l'adjacence à une rue, si bien que les quatre voisins d'une case de route se
 * faisaient bâtir et que la rue finissait emmurée. Le bâtiment suivant ne pouvait plus
 * l'atteindre et créait sa propre flaque de bitume. Mesuré sur quatorze poses :
 * **quatre cases de route, quatre composantes disjointes, masque 0 chacune.** Toute la
 * circulation en dépendait, et n'a donc jamais démarré.
 *
 * L'invariant est maintenant appliqué, pas décrit : `encirclesNetwork` refuse toute pose
 * qui priverait une composante du réseau de sa dernière case libre voisine, et
 * `distanceToNetwork` donne au placement automatique le **coût réel en bitume** de
 * chaque emplacement plutôt qu'une approximation qui se trompait de signe.
 *
 * Leçon de méthode : un commentaire qui affirme un invariant sans code pour le tenir
 * finit par décrire le contraire de ce qui se passe, et personne ne s'en aperçoit.
 *
 * **Le raccordement est un plus court chemin, pas une ligne droite.** Une ligne droite
 * traverserait les bâtiments existants ; un parcours en largeur contourne, et donne ces
 * rues qui serpentent entre les pâtés de maisons — bien plus crédible qu'une grille
 * parfaite.
 */

export interface RoadGrid {
  readonly cols: number;
  readonly rows: number;
}

export interface RoadCell {
  readonly col: number;
  readonly row: number;
}

/** Bits de connexion. L'ordre N→E→S→O est celui de la rotation horaire. */
export const NORTH = 1;
export const EAST = 2;
export const SOUTH = 4;
export const WEST = 8;

const DIRECTIONS: readonly { readonly bit: number; readonly dc: number; readonly dr: number }[] = [
  { bit: NORTH, dc: 0, dr: -1 },
  { bit: EAST, dc: 1, dr: 0 },
  { bit: SOUTH, dc: 0, dr: 1 },
  { bit: WEST, dc: -1, dr: 0 },
];

export function roadKey(col: number, row: number): string {
  return `${String(col)}:${String(row)}`;
}

export function parseRoadKey(key: string): RoadCell {
  const [col, row] = key.split(':');
  return { col: Number(col), row: Number(row) };
}

function inside(grid: RoadGrid, col: number, row: number): boolean {
  return col >= 0 && row >= 0 && col < grid.cols && row < grid.rows;
}

/**
 * Masque des connexions d'une case de route avec ses voisines.
 *
 * Une case isolée rend 0 : c'est le seul cas où aucune forme du pack ne convient, et
 * l'appelant affiche alors une impasse plutôt que rien.
 */
export function connectionMask(roads: ReadonlySet<string>, col: number, row: number): number {
  let mask = 0;
  for (const { bit, dc, dr } of DIRECTIONS) {
    if (roads.has(roadKey(col + dc, row + dr))) mask |= bit;
  }
  return mask;
}

export type RoadShape = 'road-end' | 'road-straight' | 'road-corner' | 'road-t' | 'road-cross';

export interface RoadSprite {
  readonly shape: RoadShape;
  /** Quart de tour à appliquer, 0 à 3. */
  readonly rotation: number;
}

/**
 * Masques de connexion des tuiles à leur rotation de base — **tous MESURÉS**.
 *
 * `npm run measure:roads` échantillonne le revêtement au milieu de chaque arête du
 * losange et rend ces nombres. Ils ne sont ni lus dans les noms de fichiers ni déduits
 * de l'apparence : c'est la règle 14, et elle est née précisément d'ici — mes
 * suppositions initiales (« le T ferme le nord », « le virage est nord-est ») étaient
 * fausses, et le réseau ne se raccordait pas.
 *
 * `road-straight` n'existe pas dans le pack : la famille `Road_Grass` a le carrefour, le
 * T, le virage et l'impasse, mais aucune ligne droite, et aucune autre famille n'est
 * complète. Elle est FABRIQUÉE par masquage de deux branches opposées du carrefour
 * (`npm run make:roads`), ce qui garde une seule matière sur toute la ville.
 */
const BASE_MASKS: Readonly<Record<RoadShape, number>> = {
  'road-cross': 15, // N E S O
  'road-t': 7, // N E S — fermé à l'ouest
  'road-straight': 5, // N S — fabriquée
  'road-corner': 6, // E S
  'road-end': 4, // S
};

/**
 * Un quart de tour du rendu, appliqué à un masque.
 *
 * **Antihoraire** (N → O → S → E), mesuré sur trois formes distinctes en comparant
 * chaque tuile à sa variante `-r1`. Supposer le sens horaire aurait donné des
 * intersections justes une fois sur deux — le genre de défaut qu'on met longtemps à
 * imputer à la bonne cause.
 */
function rotateMask(mask: number, steps: number): number {
  const turn = ((steps % 4) + 4) % 4;
  let value = mask & 15;
  for (let i = 0; i < turn; i++) value = ((value >> 1) | (value << 3)) & 15;
  return value;
}

/** Table exhaustive masque → (forme, rotation), construite une fois au chargement. */
const LOOKUP = ((): ReadonlyMap<number, RoadSprite> => {
  const table = new Map<number, RoadSprite>();

  // Ordre d'insertion = ordre de préférence : à masque égal, la forme la plus
  // spécifique gagne. Aucun conflit en pratique, mais l'ordre rend le résultat
  // déterministe plutôt que dépendant de l'itération d'un objet.
  const shapes: readonly RoadShape[] = [
    'road-cross',
    'road-t',
    'road-straight',
    'road-corner',
    'road-end',
  ];

  for (const shape of shapes) {
    for (let rotation = 0; rotation < 4; rotation++) {
      const mask = rotateMask(BASE_MASKS[shape], rotation);
      if (!table.has(mask)) table.set(mask, { shape, rotation });
    }
  }

  return table;
})();

/**
 * Forme et rotation correspondant à un masque de connexions.
 *
 * Une case isolée (masque 0) prend une impasse orientée par défaut : mieux vaut un bout
 * de route mal tourné qu'un trou dans le réseau.
 */
export function roadSprite(mask: number): RoadSprite {
  return LOOKUP.get(mask & 15) ?? { shape: 'road-end', rotation: 0 };
}

export interface ConnectOptions {
  readonly grid: RoadGrid;
  readonly roads: ReadonlySet<string>;
  /** Cases occupées par du bâti : la route les contourne. */
  readonly blocked: ReadonlySet<string>;
  /** Cases d'où la route peut partir — le pourtour du bâtiment qu'on vient de poser. */
  readonly from: readonly RoadCell[];
}

/**
 * Issue d'un raccordement — **et pourquoi ce n'est plus un simple tableau de cases**.
 *
 * L'ancienne version rendait des cases, et rien d'autre. Ses trois cas d'échec se
 * confondaient donc avec ses réussites :
 *
 * - `[]` voulait dire « déjà desservi », donc *tout va bien* ;
 * - `[starts[0]]` voulait dire « première route de la ville », donc *tout va bien* ;
 * - `[starts[0]]` voulait AUSSI dire « réseau inatteignable », donc *rien ne va*.
 *
 * Les deux derniers étant indistinguables, un échec de raccordement posait une case de
 * bitume isolée — masque 0, rendue en impasse orientée au hasard — au lieu d'être
 * signalé. Mesuré sur quatorze poses automatiques : **quatre flaques disjointes, aucune
 * route de plus**. C'est ce repli muet qui a caché pendant tout ce temps la véritable
 * cause (le placement automatique enclavait ses propres rues), et qui a rendu inerte le
 * moteur de circulation — aucun itinéraire ne peut exister sur des cases isolées.
 *
 * Nommer les cas ne corrige rien à lui seul ; il rend le défaut IMPOSSIBLE À IGNORER,
 * ce qui est la condition pour le corriger.
 */
export type ConnectOutcome =
  /** Le pourtour touche déjà le réseau : rien à tracer, tout va bien. */
  | { readonly kind: 'already'; readonly cells: readonly RoadCell[] }
  /** Première route de la ville : il n'y avait rien à rejoindre. */
  | { readonly kind: 'seeded'; readonly cells: readonly RoadCell[] }
  /** Raccordement tracé jusqu'au réseau existant. */
  | { readonly kind: 'connected'; readonly cells: readonly RoadCell[] }
  /** Réseau cerné par le bâti : AUCUNE case n'est posée, la pose doit être refusée. */
  | { readonly kind: 'unreachable'; readonly cells: readonly RoadCell[] };

/**
 * Trace le raccordement d'un nouveau bâtiment au réseau.
 *
 * Rend les cases à AJOUTER, dans l'ordre du tracé — c'est cet ordre qui permet
 * d'animer le déroulement du segment au lieu de le faire apparaître d'un coup.
 *
 * Un parcours en largeur depuis toutes les cases de départ à la fois : le premier
 * contact avec le réseau existant est donc le plus court raccordement possible, sans
 * avoir à comparer les candidats un par un.
 */
export function connectToNetwork(options: ConnectOptions): ConnectOutcome {
  const { grid, roads, blocked, from } = options;

  const starts = from.filter(
    (cell) => inside(grid, cell.col, cell.row) && !blocked.has(roadKey(cell.col, cell.row)),
  );
  if (starts.length === 0) return { kind: 'unreachable', cells: [] };

  // Premier bâtiment de la ville : aucune route à rejoindre, on pose la première case.
  if (roads.size === 0) {
    const seed = starts[0];
    return seed === undefined
      ? { kind: 'unreachable', cells: [] }
      : { kind: 'seeded', cells: [seed] };
  }

  /*
    ⚠️ DÉJÀ RACCORDÉ veut dire « une case du pourtour EST une route ».

    La garde testait `connectionMask(roads, pourtour) !== 0`, c'est-à-dire « une case du
    pourtour a une route pour VOISINE » — une case trop loin. Un bâtiment séparé du
    bitume par une case d'herbe se déclarait donc raccordé, et aucun segment n'était
    tracé. Il restait enclavé, définitivement, sans que rien ne le signale.

    C'est la deuxième cause racine de la fragmentation du réseau, et la plus insidieuse :
    elle ne produisait pas de flaque visible, elle produisait des bâtiments qui ne
    touchent aucune rue. Mesuré sur six poses automatiques — trois bâtiments sans
    desserte, et un réseau resté à UNE case pendant que la ville grandissait autour.

    Corollaire : le raccordement d'un bâtiment à une case d'écart trace maintenant cette
    unique case, et c'est ainsi qu'une rue s'allonge au lieu de rester un point.
  */
  for (const cell of starts) {
    if (roads.has(roadKey(cell.col, cell.row))) {
      return { kind: 'already', cells: [] };
    }
  }

  const cameFrom = new Map<string, string | null>();
  /** Direction d'arrivée sur chaque case : sert à privilégier la ligne droite. */
  const arrivedBy = new Map<string, number>();
  const queue: RoadCell[] = [];

  for (const cell of starts) {
    const key = roadKey(cell.col, cell.row);
    if (cameFrom.has(key)) continue;
    cameFrom.set(key, null);
    queue.push(cell);
  }

  // Boucle indexée assumée : la file GRANDIT pendant le parcours, ce qu'un `for…of`
  // sur un tableau ne permet pas d'exprimer — c'est le principe même du parcours en
  // largeur, pas une itération simple.
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let head = 0; head < queue.length; head++) {
    const cell = queue[head];
    if (cell === undefined) continue;
    const key = roadKey(cell.col, cell.row);

    /*
      Les directions sont explorées en commençant par CELLE D'OÙ L'ON VIENT.

      À longueur égale, un parcours en largeur choisit un chemin arbitraire, et les
      chemins arbitraires prennent la forme d'escaliers : une case à droite, une case en
      bas, et ainsi de suite. Une rue en escalier consomme autant de terrain qu'une rue
      en L mais se lit comme un défaut. Explorer d'abord la continuation rectiligne fait
      émerger des rues droites qui ne tournent qu'une fois — sans allonger le chemin
      d'une seule case, puisque la longueur reste celle du plus court.
    */
    const incoming = arrivedBy.get(key);
    const ordered =
      incoming === undefined
        ? DIRECTIONS
        : [...DIRECTIONS].sort(
            (a, b) => (b.bit === incoming ? 1 : 0) - (a.bit === incoming ? 1 : 0),
          );

    for (const { bit, dc, dr } of ordered) {
      const col = cell.col + dc;
      const row = cell.row + dr;
      if (!inside(grid, col, row)) continue;

      const nextKey = roadKey(col, row);
      if (cameFrom.has(nextKey)) continue;

      // Atteindre le réseau : on remonte le chemin et on s'arrête là.
      if (roads.has(nextKey)) {
        const path: RoadCell[] = [];
        let cursor: string | null = key;
        while (cursor !== null) {
          path.push(parseRoadKey(cursor));
          cursor = cameFrom.get(cursor) ?? null;
        }
        // Du bâtiment vers le réseau : c'est le sens dans lequel le joueur voit la
        // route se dérouler.
        return { kind: 'connected', cells: path.reverse() };
      }

      if (blocked.has(nextKey)) continue;
      cameFrom.set(nextKey, key);
      arrivedBy.set(nextKey, bit);
      queue.push({ col, row });
    }
  }

  /*
    Réseau inatteignable — et on ne pose RIEN.

    L'ancienne version posait ici la case de départ « pour ne jamais laisser un bâtiment
    sans desserte ». L'intention était juste, le remède faux : une case de bitume qui ne
    touche aucune autre n'est pas une desserte, c'est une tache. Le réseau se
    fragmentait, la circulation ne pouvait plus exister, et le joueur voyait des bouts de
    route poussés au milieu de ses maisons.

    L'appelant refuse maintenant la pose (`no-access`). Et comme `canPlace` interdit
    désormais d'encercler une rue (`encirclesNetwork`), ce cas ne devrait plus survenir
    par le chemin joueur : s'il survient, c'est un défaut à corriger, pas un état à
    maquiller.
  */
  return { kind: 'unreachable', cells: [] };
}

/**
 * Une composante du réseau se retrouverait-elle CERNÉE par le bâti ?
 *
 * ── La cause racine de tous les défauts de route ────────────────────────────
 *
 * Le placement automatique récompensait l'adjacence à une rue. Les quatre voisins d'une
 * case de route se faisaient donc bâtir l'un après l'autre, et la rue finissait
 * **emmurée** : plus aucun chemin ne pouvait l'atteindre. Le bâtiment suivant tombait
 * alors dans le repli muet de `connectToNetwork` et créait sa propre flaque isolée. En
 * boucle. Mesuré : quatorze poses, quatre cases de route, quatre composantes, aucune
 * connexion.
 *
 * Une rue n'a d'intérêt que si elle peut encore GRANDIR. On refuse donc toute pose qui
 * priverait une composante du réseau de sa dernière case libre voisine.
 *
 * ── Pourquoi par COMPOSANTE et non par case ─────────────────────────────────
 *
 * Un vrai carrefour a ses quatre voisins occupés par de la chaussée : jugée seule, la
 * case paraîtrait cernée alors qu'elle est le cœur du réseau. C'est la composante
 * entière qui doit garder une ouverture, pas chacune de ses cases.
 *
 * ── Pourquoi on ne teste QUE les composantes touchées ───────────────────────
 *
 * Une pose ne retire que ses propres cases au domaine libre : seules les composantes qui
 * bordent l'emprise peuvent perdre leur dernière ouverture. Inspecter le réseau entier à
 * chaque candidat coûterait `cases × routes` à chaque placement automatique — et
 * `findAutoPlacement` évalue toute la grille.
 */
export function encirclesNetwork(options: {
  readonly grid: RoadGrid;
  readonly roads: ReadonlySet<string>;
  /** Cases bâties APRÈS la pose envisagée, emprise du nouveau bâtiment comprise. */
  readonly occupied: ReadonlySet<string>;
  /** Emprise du bâtiment envisagé — sert à ne visiter que les composantes concernées. */
  readonly footprint: readonly RoadCell[];
}): boolean {
  const { grid, roads, occupied, footprint } = options;
  if (roads.size === 0) return false;

  // Composantes bordant l'emprise : les seules que cette pose puisse étouffer.
  const seeds: string[] = [];
  for (const cell of footprint) {
    for (const { dc, dr } of DIRECTIONS) {
      const key = roadKey(cell.col + dc, cell.row + dr);
      if (roads.has(key)) seeds.push(key);
    }
  }
  if (seeds.length === 0) return false;

  const visited = new Set<string>();

  for (const seed of seeds) {
    if (visited.has(seed)) continue;

    // Parcours de la composante, en cherchant une ouverture au passage.
    const queue = [seed];
    visited.add(seed);
    let open = false;

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let head = 0; head < queue.length; head++) {
      const current = queue[head];
      if (current === undefined) continue;
      const cell = parseRoadKey(current);

      for (const { dc, dr } of DIRECTIONS) {
        const col = cell.col + dc;
        const row = cell.row + dr;
        if (!inside(grid, col, row)) continue;

        const key = roadKey(col, row);
        if (roads.has(key)) {
          if (!visited.has(key)) {
            visited.add(key);
            queue.push(key);
          }
          continue;
        }

        // Une case libre au contact : la composante respire encore.
        if (!occupied.has(key)) open = true;
      }
    }

    if (!open) return true;
  }

  return false;
}

/**
 * Combien de cases libres restent ATTEIGNABLES depuis le réseau ?
 *
 * ── Trois invariants essayés, et pourquoi les deux premiers ont échoué ──────
 *
 * **« Une case libre au contact du réseau »** — trop peu. Le réseau finissait avec une
 * unique case libre voisine, elle-même en cul-de-sac : la pose suivante devait la
 * bitumer pour se raccorder, et le réseau se retrouvait scellé sans qu'aucun contrôle
 * ne l'ait vu venir. Mesuré : la partie se figeait à **33 bâtiments sur 1 156 cases**.
 *
 * **« Au moins N cases libres atteignables »** — mieux, et encore insuffisant. Le seuil
 * était satisfait par des poches intérieures de quelques cases, pendant que le bâti
 * formait un mur continu entre la rue et les trois quarts vides de l'île. Mesuré :
 * blocage à 31 bâtiments, `no-space` sur une grille 34 × 34 dont 900 cases étaient
 * libres — mais murées.
 *
 * **Ce qui marche : comparer AVANT et APRÈS.** La question n'est pas « combien reste-t-il
 * de terrain », c'est **« cette pose en RETRANCHE-t-elle plus qu'elle n'en occupe ? »**.
 * Un bâtiment a le droit de consommer ses propres cases et sa rue ; il n'a pas le droit
 * de couper la ville du reste de son île. La différence entre les deux comptes le dit
 * exactement, sans seuil arbitraire et sans profondeur d'analyse à choisir.
 *
 * Le parcours est complet — pas d'arrêt anticipé — parce que c'est le NOMBRE qui porte
 * l'information. Il coûte une passe sur la grille, et le placement automatique n'en
 * paie que deux par pose dans le cas courant.
 */
export function reachableFreeCount(
  grid: RoadGrid,
  roads: ReadonlySet<string>,
  occupied: ReadonlySet<string>,
): number {
  if (roads.size === 0) {
    // Aucune rue : tout le terrain libre est constructible, la première pose créera
    // la première route où elle voudra.
    let free = 0;
    for (let col = 0; col < grid.cols; col++) {
      for (let row = 0; row < grid.rows; row++) {
        if (!occupied.has(roadKey(col, row))) free++;
      }
    }
    return free;
  }

  const seen = new Set<string>();
  const queue: RoadCell[] = [];
  let count = 0;

  for (const key of roads) {
    seen.add(key);
    queue.push(parseRoadKey(key));
  }

  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let head = 0; head < queue.length; head++) {
    const cell = queue[head];
    if (cell === undefined) continue;

    for (const { dc, dr } of DIRECTIONS) {
      const col = cell.col + dc;
      const row = cell.row + dr;
      if (!inside(grid, col, row)) continue;

      const key = roadKey(col, row);
      if (seen.has(key) || occupied.has(key)) continue;

      seen.add(key);
      // Une case de chaussée se traverse mais n'est pas du terrain libre : c'est ainsi
      // que l'inondation longe la rue au lieu de s'arrêter à sa première case.
      if (!roads.has(key)) count++;
      queue.push({ col, row });
    }
  }

  return count;
}

/**
 * Distance, en cases, de chaque case libre à la route la plus proche.
 *
 * ── Pourquoi cette carte existe ─────────────────────────────────────────────
 *
 * Le placement automatique notait chaque emplacement avec des approximations : « bonus
 * si une route touche », « bonus par route adjacente », « bonus par voisin bâti ». La
 * deuxième récompensait les cases entourées de bitume — c'est-à-dire précisément celles
 * qui bouchent une rue. Aucune de ces notes ne mesurait ce qui compte vraiment :
 * **combien de bitume neuf cette pose va coûter.**
 *
 * Un seul parcours en largeur depuis tout le réseau à la fois donne la réponse exacte
 * pour toute la grille, pour le prix d'une passe. La note cesse d'être un proxy.
 *
 * Les cases bâties sont infranchissables : la distance obtenue est celle que la route
 * devra réellement parcourir en contournant, pas une distance à vol d'oiseau.
 */
export function distanceToNetwork(
  grid: RoadGrid,
  roads: ReadonlySet<string>,
  occupied: ReadonlySet<string>,
): ReadonlyMap<string, number> {
  const distance = new Map<string, number>();
  const queue: RoadCell[] = [];

  for (const key of roads) {
    distance.set(key, 0);
    queue.push(parseRoadKey(key));
  }

  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let head = 0; head < queue.length; head++) {
    const cell = queue[head];
    if (cell === undefined) continue;
    const step = (distance.get(roadKey(cell.col, cell.row)) ?? 0) + 1;

    for (const { dc, dr } of DIRECTIONS) {
      const col = cell.col + dc;
      const row = cell.row + dr;
      if (!inside(grid, col, row)) continue;

      const key = roadKey(col, row);
      if (distance.has(key) || occupied.has(key)) continue;

      distance.set(key, step);
      queue.push({ col, row });
    }
  }

  return distance;
}

/**
 * Reconstruit un réseau routier pour une ville déjà bâtie.
 *
 * **Pourquoi cette migration existe.** Les routes se tracent à la pose. Une ville
 * construite avant leur arrivée n'en a donc aucune, et la feature reste strictement
 * invisible pour tout joueur qui a déjà joué — c'est-à-dire pour le seul joueur qui
 * compte aujourd'hui. Livrer la feature sans la migration revenait à ne rien livrer.
 *
 * On rejoue le raccordement bâtiment par bâtiment, dans l'ordre de pose (`placedAt`),
 * exactement comme si la ville s'était construite avec les routes depuis le début. Le
 * résultat est donc le même réseau que celui qu'on aurait obtenu naturellement, et non
 * une grille plaquée après coup.
 */
export function rebuildNetwork(options: {
  readonly grid: RoadGrid;
  /** Bâtiments, avec les cases occupées par chacun. */
  readonly buildings: readonly {
    readonly placedAt: number;
    readonly cells: readonly RoadCell[];
  }[];
}): readonly string[] {
  const { grid } = options;
  const ordered = [...options.buildings].sort((a, b) => a.placedAt - b.placedAt);

  /*
    L'occupation se construit AU FUR ET À MESURE, pas d'avance.

    Elle était remplie avec TOUS les bâtiments avant la boucle : le raccordement du
    premier devait alors contourner des bâtiments qui n'existaient pas encore le jour où
    il a été posé. Le réseau reconstruit n'était donc pas « celui qu'on aurait obtenu
    naturellement » — ce que la documentation promettait pourtant — mais un réseau tracé
    dans une ville déjà pleine, plus long et plus tortueux.

    Rejouer l'histoire, c'est ne connaître à chaque étape que ce qui existait alors.
  */
  const occupied = new Set<string>();
  const roads = new Set<string>();

  for (const building of ordered) {
    for (const cell of building.cells) occupied.add(roadKey(cell.col, cell.row));

    const around: RoadCell[] = [];
    for (const cell of building.cells) {
      for (const { dc, dr } of DIRECTIONS) {
        const col = cell.col + dc;
        const row = cell.row + dr;
        if (!occupied.has(roadKey(col, row))) around.push({ col, row });
      }
    }

    const outcome = connectToNetwork({ grid, roads, blocked: occupied, from: around });
    for (const cell of outcome.cells) {
      roads.add(roadKey(cell.col, cell.row));
    }
  }

  return [...roads];
}

/**
 * Élague les segments de route qui ne desservent plus rien.
 *
 * Vendre ou déplacer un bâtiment laisse derrière lui la rue qui y menait. Ces impasses
 * ne mènent nulle part, occupent du terrain constructible, et donnent à la ville cet
 * aspect de rues qui serpentent dans le vide — le gaspillage le plus visible.
 *
 * On retire itérativement les cases de **degré 1 ou 0** qui ne touchent aucun bâtiment :
 * couper une extrémité en crée souvent une nouvelle, d'où la répétition jusqu'à ce que
 * plus rien ne bouge. Une case de degré 2 ou plus n'est jamais retirée — ce serait
 * couper le réseau en deux.
 */
export function pruneRoads(
  roads: readonly string[],
  served: ReadonlySet<string>,
): readonly string[] {
  const network = new Set(roads);
  let changed = true;

  while (changed) {
    changed = false;

    for (const key of [...network]) {
      // Une case qui borde un bâtiment est utile, quel que soit son degré.
      if (served.has(key)) continue;

      const cell = parseRoadKey(key);
      let degree = 0;
      for (const { dc, dr } of DIRECTIONS) {
        if (network.has(roadKey(cell.col + dc, cell.row + dr))) degree++;
      }

      if (degree <= 1) {
        network.delete(key);
        changed = true;
      }
    }
  }

  return [...network];
}

/** Cases de route bordant au moins un bâtiment — celles qu'il ne faut jamais élaguer. */
export function servedCells(
  buildings: readonly { readonly cells: readonly RoadCell[] }[],
): ReadonlySet<string> {
  const served = new Set<string>();

  for (const building of buildings) {
    for (const cell of building.cells) {
      for (const { dc, dr } of DIRECTIONS) {
        served.add(roadKey(cell.col + dc, cell.row + dr));
      }
    }
  }

  return served;
}
